// AppFlagsContext.js
// Provides app-level (global) feature flags as a React context.
//
// Backed by AppSettings rows on doorapi and the GET /settings/feature_flags
// endpoint. The flag state is loaded once on app start and refreshed via
// the `refresh()` function (call after toggling a checkbox in Setup so the
// UI immediately reflects the new value).
//
// Usage:
//   import { useAppFlags } from '../utils/AppFlagsContext';
//   const { flags, refresh } = useAppFlags();
//   if (flags.promotions_enabled) { ... }

import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authFetch } from './api';
import Cookies from 'js-cookie';

const DEFAULTS = {
  // Match server-side defaults in routes/settings.py _get_bool_setting().
  dealer_enabled: false,
  promotions_enabled: true,
  financing_enabled: true,
};

const AppFlagsContext = createContext({ flags: DEFAULTS, refresh: () => {} });

export const AppFlagsProvider = ({ children }) => {
  const [flags, setFlags] = useState(DEFAULTS);

  const refresh = useCallback(() => {
    if (!Cookies.get('access_token')) return;
    authFetch('/settings/feature_flags')
      .then(data => {
        if (!data || typeof data !== 'object') return;
        setFlags(prev => ({ ...prev, ...data }));
      })
      .catch(() => {
        // Fall back to defaults if the backend is unreachable - the UI
        // should remain functional even with an offline doorapi.
      });
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <AppFlagsContext.Provider value={{ flags, refresh }}>
      {children}
    </AppFlagsContext.Provider>
  );
};

export const useAppFlags = () => useContext(AppFlagsContext);
